import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';

import { UiKitModule } from '../../../shared/modules/ui-kit/ui-kit.module';
import { saveFile } from '../../../helpers/file-download';
import { ProntoPagoStateService } from '../../../services/pronto-pago-state.service';
import {
  CodTipoDetalle,
  ProntoPagoCuota,
  ProntoPagoDeuda,
  ProntoPagoRestitucionService,
  ProntoPagoResumen
} from '../../../services/pronto-pago-restitucion.service';

/**
 * Paso 1 de 2 del flujo "Pronto Pago": PROPUESTA.
 *
 * El ex afiliado ve su deuda y elige una modalidad:
 *   - "Compensando tu deuda"   → codTipoDetalle = 1
 *   - "Sin compensar tu deuda" → codTipoDetalle = 0
 * Según la elección se muestra la tabla de cuotas filtrada y el "Resumen de tu oferta".
 * Al aceptar el acuerdo, emite `continuar` para que el contenedor avance a Verificación.
 */
@Component({
  selector: 'app-pronto-pago-propuesta',
  standalone: true,
  imports: [CommonModule, FormsModule, UiKitModule],
  templateUrl: './propuesta.component.html',
  styleUrl: './propuesta.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class PropuestaComponent implements OnInit {

  @Output() continuar = new EventEmitter<void>();

  cargando = true;
  error = false;

  resumen!: ProntoPagoResumen;
  private cuotas: ProntoPagoCuota[] = [];
  deuda: ProntoPagoDeuda[] = [];

  modalidad: CodTipoDetalle | null = null;

  private readonly deudaExpandida = new Set<ProntoPagoDeuda>();

  acuerdoDescargado = false;
  acuerdoAceptado = false;
  descargandoAcuerdo = false;
  errorDescargaAcuerdo = false;

  paginaActual = 1;
  readonly cuotasPorPagina = 8;

  constructor(
    private readonly prontoPagoService: ProntoPagoRestitucionService,
    private readonly estado: ProntoPagoStateService
  ) { }

  ngOnInit(): void {
    this.cargarDatos();
  }

  cargarDatos(): void {
    this.cargando = true;
    this.error = false;
    forkJoin([
      this.prontoPagoService.getResumen(),
      this.prontoPagoService.getDetalleCuotas(),
      this.prontoPagoService.getDetalleDeuda()
    ]).subscribe({
      next: ([resumen, cuotas, deuda]) => {
        this.resumen = resumen;
        this.cuotas = cuotas;
        this.deuda = deuda;
        this.aplicarModalidadPorDefecto();
        this.cargando = false;
      },
      error: () => {
        this.cargando = false;
        this.error = true;
      }
    });
  }

  reintentar(): void {
    this.cargarDatos();
  }

  /** Hay algo que compensar. Si no, la elección de modalidad no tiene sentido. */
  get tieneDeuda(): boolean {
    return this.deuda.length > 0;
  }

  /**
   * Sin deudas no hay nada que compensar: se oculta el bloque de elección y se entra
   * directamente en "sin compensar", de modo que la tabla de cuotas se pinta al llegar.
   */
  private aplicarModalidadPorDefecto(): void {
    if (!this.tieneDeuda) {
      this.modalidad = 0;
    }
  }

  get valorUf(): number {
    return this.resumen && this.resumen.ufReferencia ? this.resumen.ufReferencia : 0;
  }

  seleccionarModalidad(cod: CodTipoDetalle): void {
    if (this.modalidad === cod) {
      return;
    }
    this.modalidad = cod;
    this.paginaActual = 1;
    this.acuerdoDescargado = false;
    this.acuerdoAceptado = false;
    this.errorDescargaAcuerdo = false;
  }

  get compensando(): boolean {
    return this.modalidad === 1;
  }

  toggleDetalleDeuda(d: ProntoPagoDeuda): void {
    if (this.deudaExpandida.has(d)) {
      this.deudaExpandida.delete(d);
    } else {
      this.deudaExpandida.add(d);
    }
  }

  estaExpandida(d: ProntoPagoDeuda): boolean {
    return this.deudaExpandida.has(d);
  }

  get cuotasModalidad(): ProntoPagoCuota[] {
    return this.modalidad === null
      ? []
      : this.cuotas.filter(c => c.codTipoDetalle === this.modalidad);
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.cuotasModalidad.length / this.cuotasPorPagina));
  }

  get paginas(): number[] {
    return Array.from({ length: this.totalPaginas }, (_, i) => i + 1);
  }

  get cuotasPagina(): ProntoPagoCuota[] {
    const inicio = (this.paginaActual - 1) * this.cuotasPorPagina;
    return this.cuotasModalidad.slice(inicio, inicio + this.cuotasPorPagina);
  }

  irAPagina(n: number): void {
    if (n >= 1 && n <= this.totalPaginas) {
      this.paginaActual = n;
    }
  }

  get rangoCuotas(): string {
    const lista = this.cuotasModalidad;
    if (!lista.length) {
      return '';
    }
    return `Cuotas N° ${lista[0].numCuota}–${lista[lista.length - 1].numCuota}`;
  }

  // Totales de la tabla de cuotas (sobre TODAS las cuotas de la modalidad, no solo la página
  // visible). Suman las columnas mostradas, por lo que cuadran con el "Resumen de tu oferta".
  get totalCuotasUf(): number {
    return this.cuotasModalidad.reduce((acc, c) => acc + c.cantidadDevengarUf, 0);
  }
  get totalDevengarClp(): number {
    return this.cuotasModalidad.reduce((acc, c) => acc + c.montoDevengarClp, 0);
  }
  get totalDescuentoClp(): number {
    return this.cuotasModalidad.reduce((acc, c) => acc + c.montoDescuentoClp, 0);
  }
  get totalNetoClp(): number {
    return this.cuotasModalidad
      .filter(c => c.glsTipoPago === 'Pago anticipado')
      .reduce((acc, c) => acc + c.montoNetoDevengado, 0);
  }

  get montoTotalClp(): number {
    return this.resumen ? this.resumen.montoDevengarClp : 0;
  }

  get montoTotalDescuentoSinCompensacionClp(): number {
    return this.resumen ? this.resumen.montoTotalDescuentoSinCompensacionClp : 0;
  }

  get montoTotalDescuentoConCompensacionClp(): number {
    return this.resumen ? this.resumen.montoTotalDescuentoConCompensacionClp : 0;
  }

  get compensaDeudaClp(): number {
    return this.resumen ? this.resumen.montoTotalDeudaCompensarClp : 0;
  }

  get liquidoConCompensacionPesos(): number {
    return this.resumen ? this.resumen.liquidoConCompensacionPesos : 0;
  }

  get liquidoSinCompensacionPesos(): number {
    return this.resumen ? this.resumen.liquidoSinCompensacionPesos : 0;
  }

  get deudaBrutoTotalClp(): number {
    return this.deuda.reduce((acc, d) => acc + d.montoDeudaBrutoClp, 0);
  }
  get deudaInteresesTotalClp(): number {
    return this.deuda.reduce((acc, d) => acc + d.montoInteresesClp, 0);
  }
  get deudaRecargosTotalClp(): number {
    return this.deuda.reduce((acc, d) => acc + d.montoRecargosClp, 0);
  }
  get deudaTotalClp(): number {
    return this.deuda.reduce((acc, d) => acc + d.montoDeudaTotalClp, 0);
  }

  get ahorroCondonacionClp(): number {
    return this.deudaTotalClp - this.deudaBrutoTotalClp;
  }

  get acuerdoNombreArchivo(): string {
    return this.compensando
      ? 'Acuerdo Compensacion y Pago Anticipado.pdf'
      : 'Acuerdo Pago Anticipado.pdf';
  }

  descargarAcuerdo(): void {
    if (this.modalidad === null || this.descargandoAcuerdo) {
      return;
    }
    this.descargandoAcuerdo = true;
    this.errorDescargaAcuerdo = false;
    this.prontoPagoService.descargarAcuerdo(this.compensando).subscribe({
      next: res => {
        this.descargandoAcuerdo = false;
        if (res.body) {
          saveFile(res.body, this.acuerdoNombreArchivo, 'pdf');
          this.acuerdoDescargado = true;
        } else {
          this.errorDescargaAcuerdo = true;
        }
      },
      error: () => {
        this.descargandoAcuerdo = false;
        this.errorDescargaAcuerdo = true;
      }
    });
  }

  get puedeContinuar(): boolean {
    return this.modalidad !== null && this.acuerdoDescargado && this.acuerdoAceptado;
  }

  continuarAVerificacion(): void {
    if (!this.puedeContinuar) {
      return;
    }
    this.estado.codTipoDetalleSeleccionado = this.modalidad;
    this.continuar.emit();
  }
}
